import { useEffect, useState } from "react";
import { RefreshCw, Mail, Phone, Paperclip, FileText, Trash2, Check, MailOpen } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { toast } from "sonner";

const isImage = (ct) => ct && ct.startsWith("image/") && ct !== "image/svg+xml";
const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export const QuotesTab = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes");
      setQuotes(data);
    } catch {
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setRead = async (id, read) => {
    setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, read } : q)));
    try {
      await api.put(`/quotes/${id}/read`, { read });
    } catch {
      toast.error("Couldn't update. Please try again.");
      load();
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete the quote request from ${name}? This can't be undone.`)) return;
    const prev = quotes;
    setQuotes((q) => q.filter((x) => x.id !== id));
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Quote request deleted");
    } catch {
      toast.error("Couldn't delete. Please try again.");
      setQuotes(prev);
    }
  };

  const unread = quotes.filter((q) => !q.read).length;

  return (
    <div data-testid="quotes-tab">
      <div className="flex items-center justify-between mb-8">
        <p className="text-navy/60 text-sm tracking-[0.1em]">
          {quotes.length} REQUEST{quotes.length === 1 ? "" : "S"}
          {unread > 0 && <span className="ml-3 inline-block bg-gold text-ink text-xs font-bold px-2 py-0.5 rounded-full" data-testid="unread-count">{unread} NEW</span>}
        </p>
        <button onClick={load} data-testid="admin-refresh" className="flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold transition-colors">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-navy/50">Loading…</p>
      ) : quotes.length === 0 ? (
        <div className="bg-white border border-line p-12 text-center text-navy/60" data-testid="quotes-empty">No quote requests yet.</div>
      ) : (
        <div className="space-y-5">
          {quotes.map((q) => (
            <div key={q.id} className={`bg-white border p-6 ${q.read ? "border-line" : "border-l-4 border-l-gold border-y-line border-r-line"}`} data-testid={`admin-quote-${q.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-display text-xl tracking-[0.03em]">{q.name}</div>
                    {!q.read && <span className="bg-gold text-ink text-[10px] font-bold px-2 py-0.5 tracking-[0.1em]" data-testid={`new-badge-${q.id}`}>NEW</span>}
                  </div>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-navy/70">
                    <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 hover:text-gold"><Mail size={14} /> {q.email}</a>
                    {q.phone && <span className="flex items-center gap-1.5"><Phone size={14} /> {q.phone}</span>}
                  </div>
                </div>
                <div className="text-right">
                  <span className="inline-block bg-navy text-white text-xs px-3 py-1 tracking-[0.08em]">{q.project_type}</span>
                  {q.quantity && <div className="text-navy/60 text-sm mt-1">Qty: {q.quantity}</div>}
                  <div className="text-navy/40 text-xs mt-1">{fmt(q.created_at)}</div>
                </div>
              </div>

              {q.details && <p className="text-navy/70 text-sm mt-4 border-t border-line pt-4 whitespace-pre-wrap">{q.details}</p>}

              {q.files?.length > 0 && (
                <div className="mt-4 border-t border-line pt-4">
                  <div className="flex items-center gap-2 text-xs tracking-[0.14em] text-navy/60 font-semibold mb-3">
                    <Paperclip size={13} /> {q.files.length} FILE{q.files.length === 1 ? "" : "S"}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                    {q.files.map((f) => (
                      <a key={f.id} href={resolveImg(f.url)} target="_blank" rel="noreferrer" className="block border border-line group" data-testid={`admin-file-${f.id}`}>
                        <div className="aspect-square bg-[#e9e6dd] flex items-center justify-center overflow-hidden">
                          {isImage(f.content_type)
                            ? <img src={resolveImg(f.url)} alt={f.original_filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                            : <FileText size={28} className="text-navy/50" />}
                        </div>
                        <div className="px-2 py-1 text-[10px] text-navy/60 truncate">{f.original_filename}</div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 mt-5 border-t border-line pt-4">
                <button
                  onClick={() => setRead(q.id, !q.read)}
                  data-testid={`toggle-read-${q.id}`}
                  className="flex items-center gap-1.5 text-xs font-semibold tracking-[0.08em] text-navy hover:text-gold transition-colors"
                >
                  {q.read ? <><MailOpen size={14} /> MARK AS UNREAD</> : <><Check size={14} /> MARK AS READ</>}
                </button>
                <button
                  onClick={() => remove(q.id, q.name)}
                  data-testid={`delete-quote-${q.id}`}
                  className="flex items-center gap-1.5 text-xs font-semibold tracking-[0.08em] text-red-600 hover:text-red-700 transition-colors ml-auto"
                >
                  <Trash2 size={14} /> DELETE
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
